import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { Target, Plus, Trash2, CheckCircle, X, Clock } from '../../../components/icons';
import { fetchGoals, createGoal, updateGoal, deleteGoal, type Goal } from '../../../lib/progress';
import { LoadingSpinner } from '../../../components/LoadingSpinner';

const STATUS_LABELS: Record<Goal['status'], string> = {
  active: 'Aktif',
  completed: 'Tamamlandı',
  paused: 'Duraklatıldı',
  abandoned: 'Bırakıldı',
};

const STATUS_STYLES: Record<Goal['status'], string> = {
  active: 'bg-sage-100 text-sage-700',
  completed: 'bg-blue-100 text-blue-700',
  paused: 'bg-amber-100 text-amber-700',
  abandoned: 'bg-red-100 text-red-700',
};

export function GoalsViewTR() {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [form, setForm] = useState({ title: '', description: '', target_date: '', status: 'active' as Goal['status'] });

  useEffect(() => {
    if (!user) return;
    fetchGoals(user.id).then(setGoals).finally(() => setIsLoading(false));
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !form.title.trim()) return;
    if (editingGoal) {
      await updateGoal(editingGoal.id, form);
      setGoals(prev => prev.map(g => g.id === editingGoal.id ? { ...g, ...form } : g));
    } else {
      const created = await createGoal(user.id, form);
      if (created) setGoals(prev => [created, ...prev]);
    }
    setShowForm(false);
    setEditingGoal(null);
    setForm({ title: '', description: '', target_date: '', status: 'active' });
  };

  const handleEdit = (goal: Goal) => {
    setEditingGoal(goal);
    setForm({ title: goal.title, description: goal.description || '', target_date: goal.target_date || '', status: goal.status });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    await deleteGoal(id);
    setGoals(prev => prev.filter(g => g.id !== id));
  };

  const handleStatusCycle = async (goal: Goal) => {
    const order: Goal['status'][] = ['active', 'completed', 'paused', 'abandoned'];
    const next = order[(order.indexOf(goal.status) + 1) % order.length];
    await updateGoal(goal.id, { status: next });
    setGoals(prev => prev.map(g => g.id === goal.id ? { ...g, status: next } : g));
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingGoal(null);
    setForm({ title: '', description: '', target_date: '', status: 'active' });
  };

  if (isLoading) return <LoadingSpinner message="Hedefler yükleniyor..." />;

  return (
    <div className="p-6 md:p-8 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
            <Target className="w-5 h-5 text-sage-600" />
          </div>
          <div>
            <h1 className="freud-heading-xl text-chocolate-900">Hedefler</h1>
            <p className="text-sm text-chocolate-500">{goals.filter(g => g.status === 'active').length} aktif</p>
          </div>
        </div>
        <button onClick={() => { setEditingGoal(null); setShowForm(true); }} className="freud-btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Yeni Hedef
        </button>
      </div>

      {showForm && (
        <div className="freud-card-elevated p-6 mb-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-4">{editingGoal ? 'Hedefi Düzenle' : 'Yeni Hedef'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="freud-label">Başlık *</label>
              <input className="freud-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Ne başarmak istiyorsunuz?" required />
            </div>
            <div>
              <label className="freud-label">Açıklama</label>
              <textarea className="freud-input" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Bu neden önemli?" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="freud-label">Hedef Tarih</label>
                <input type="date" className="freud-input" value={form.target_date} onChange={e => setForm(p => ({ ...p, target_date: e.target.value }))} />
              </div>
              <div>
                <label className="freud-label">Durum</label>
                <select className="freud-input" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value as Goal['status'] }))}>
                  {(Object.keys(STATUS_LABELS) as Goal['status'][]).map(s => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="freud-btn-primary">{editingGoal ? 'Değişiklikleri Kaydet' : 'Hedef Oluştur'}</button>
              <button type="button" onClick={handleCancel} className="freud-btn-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {goals.length === 0 ? (
        <div className="freud-card-elevated p-12 text-center">
          <Target className="w-12 h-12 text-cream-400 mx-auto mb-3" />
          <p className="text-chocolate-500">Henüz hedef yok. Başlamak için ilk hedefinizi belirleyin.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {goals.map(goal => (
            <div key={goal.id} className="freud-card-elevated p-4 flex items-start gap-4">
              <button onClick={() => handleStatusCycle(goal)} className="mt-0.5 flex-shrink-0" title="Durumu değiştirmek için tıklayın">
                {goal.status === 'completed'
                  ? <CheckCircle className="w-5 h-5 text-sage-500" />
                  : goal.status === 'paused'
                  ? <Clock className="w-5 h-5 text-amber-500" />
                  : goal.status === 'abandoned'
                  ? <X className="w-5 h-5 text-red-400" />
                  : <CheckCircle className="w-5 h-5 text-chocolate-200" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[goal.status]}`}>{STATUS_LABELS[goal.status]}</span>
                  {goal.target_date && (
                    <span className="text-[11px] text-chocolate-400">son: {new Date(goal.target_date).toLocaleDateString('tr-TR')}</span>
                  )}
                </div>
                <p className={`mt-1 font-medium text-chocolate-900 ${goal.status === 'abandoned' ? 'line-through text-chocolate-400' : ''}`}>{goal.title}</p>
                {goal.description && <p className="text-sm text-chocolate-500 mt-0.5">{goal.description}</p>}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => handleEdit(goal)} className="p-1.5 rounded-lg text-chocolate-400 hover:bg-cream-100 hover:text-chocolate-700 transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                </button>
                <button onClick={() => handleDelete(goal.id)} className="p-1.5 rounded-lg text-chocolate-400 hover:bg-red-50 hover:text-red-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
